import type { PaginatedApiResult } from '../api/pbxSettingsApi.types'
import type {
  EditPbxFormState,
  PbxFormValues,
  PbxSystemSettingsV2,
  PbxV2,
  PhoneNumberOption,
  SelectOption,
  SystemSettingsFormValues,
} from '../types/pbxSettings.types'
import {
  CALL_PARK_TIMEOUT_MAX,
  CALL_PARK_TIMEOUT_MIN,
  CALL_PARK_TIMEOUT_RECOMMENDED,
  DAY_NIGHT_SLOT_COUNT,
  EHR_OPTIONS,
  INTERNAL_ONLY_EHR_VALUES,
} from './pbxSettings.constants'

/**
 * EHR options visible to the given audience. Internal staff see the full list.
 * Customers see only released EHRs — unreleased systems are reserved for staff
 * tagging (integrations' pre-launch marketing pulls). A PBX already tagged with
 * an unreleased EHR keeps its properly labeled option so the value round-trips
 * instead of being silently wiped on save.
 */
export const getVisibleEhrOptions = (
  isInternalUser: boolean,
  currentEhrType: string,
): SelectOption[] => {
  if (isInternalUser) return EHR_OPTIONS
  return EHR_OPTIONS.filter(
    (option) =>
      !INTERNAL_ONLY_EHR_VALUES.has(option.value) ||
      option.value === currentEhrType,
  )
}

export const toSelectValue = (
  value: string | number | null | undefined,
): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

export const toNumberInputValue = (
  value: number | null | undefined,
): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

export const parseNullableInt = (value: string): number | null => {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseInt(trimmed, 10)
  if (Number.isNaN(parsed)) return null
  return parsed
}

/**
 * Legacy parity: the old admin form normalizes initial values into supported bounds
 * (recommended default for missing/0, then clamp to min/max).
 */
export const normalizeCallParkTimeoutForLegacy = (
  value: string | number | null | undefined,
): string => {
  const parsed = Number.parseInt(String(value ?? ''), 10)

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return String(CALL_PARK_TIMEOUT_RECOMMENDED)
  }
  if (parsed < CALL_PARK_TIMEOUT_MIN) {
    return String(CALL_PARK_TIMEOUT_MIN)
  }
  if (parsed > CALL_PARK_TIMEOUT_MAX) {
    return String(CALL_PARK_TIMEOUT_MAX)
  }
  return String(parsed)
}

export const stringifyError = (
  errorData: unknown,
  fallback: string,
): string => {
  if (typeof errorData === 'string') return errorData

  if (errorData && typeof errorData === 'object') {
    const apiError = errorData as {
      message?: string
      detail?: string
      parameters?: Array<{ name?: string; message?: string }>
    }
    if (apiError.parameters?.length) {
      const [firstParameterError] = apiError.parameters
      const parameterName = firstParameterError.name ?? 'field'
      const parameterMessage = firstParameterError.message ?? 'Invalid value'
      return `${parameterName}: ${parameterMessage}`
    }
    if (apiError.message) return apiError.message
    if (apiError.detail) return apiError.detail
  }

  return fallback
}

export const cloneFormState = (state: EditPbxFormState): EditPbxFormState =>
  JSON.parse(JSON.stringify(state)) as EditPbxFormState

/** Strip non-digits and remove leading US country code so 10-digit and E.164 formats match */
export const normalizePhoneForCompare = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1)
  return digits
}

export const resolvePhoneNumberValue = (
  currentValue: string,
  phoneNumbers: PhoneNumberOption[],
): string => {
  if (!currentValue) return currentValue

  if (phoneNumbers.some((pn) => String(pn.number) === currentValue)) {
    return currentValue
  }

  const idMatch = phoneNumbers.find((pn) => String(pn.id) === currentValue)
  if (idMatch) return String(idMatch.number)

  const normalized = normalizePhoneForCompare(currentValue)
  const fuzzyMatch = phoneNumbers.find(
    (pn) => normalizePhoneForCompare(String(pn.number)) === normalized,
  )
  if (fuzzyMatch) return String(fuzzyMatch.number)

  return currentValue
}

export const buildPbxPatchPayload = (
  pbxValues: PbxFormValues,
): Record<string, unknown> => ({
  name: pbxValues.name.trim(),
  company_id: parseNullableInt(pbxValues.company_id),
  company_name: pbxValues.company_name.trim(),
  server_group_id: parseNullableInt(pbxValues.server_group_id),
  provider_gateway_id: parseNullableInt(pbxValues.provider_gateway_id),
  emergency_provider_gateway_id: parseNullableInt(
    pbxValues.emergency_provider_gateway_id,
  ),
  international_provider_gateway_id: parseNullableInt(
    pbxValues.international_provider_gateway_id,
  ),
  active: pbxValues.active,
  maximum_allowed_channels: parseNullableInt(
    pbxValues.maximum_allowed_channels,
  ),
  maximum_lines_in: parseNullableInt(pbxValues.maximum_lines_in),
  maximum_lines_out: parseNullableInt(pbxValues.maximum_lines_out),
  call_log_retention_period_in_months: parseNullableInt(
    pbxValues.call_log_retention_period_in_months,
  ),
  industry_type: pbxValues.industry_type.trim() || null,
  ehr_type: pbxValues.ehr_type.trim() || null,
  preferred_codec: pbxValues.preferred_codec.trim() || null,
  international: pbxValues.international,
  is_grove: pbxValues.is_grove,
  day_night: pbxValues.day_night,
  ...Object.fromEntries(
    Array.from({ length: DAY_NIGHT_SLOT_COUNT }, (_, i) => [
      `day_night_${i}`,
      pbxValues.day_night_slots[i] ?? false,
    ]),
  ),
})

export const buildSystemSettingsPatchPayload = (
  systemSettingsValues: SystemSettingsFormValues,
): Record<string, unknown> => ({
  default_time_zone: systemSettingsValues.default_time_zone.trim(),
  extension_digits: parseNullableInt(systemSettingsValues.extension_digits),
  default_area_code: systemSettingsValues.default_area_code.trim(),
  default_caller_id: systemSettingsValues.default_caller_id.trim() || null,
  default_send_fax_number:
    systemSettingsValues.default_send_fax_number.trim() || null,
  default_fax_extension_id: parseNullableInt(
    systemSettingsValues.default_fax_extension_id,
  ),
  default_hold_music_playlist: parseNullableInt(
    systemSettingsValues.default_hold_music_playlist,
  ),
  default_emergency_number:
    systemSettingsValues.default_emergency_number.trim(),
  default_emergency_name: systemSettingsValues.default_emergency_name.trim(),
  call_park_timeout: systemSettingsValues.call_park_timeout.trim(),
  enable_inbound_recording: systemSettingsValues.enable_inbound_recording,
  enable_outbound_recording: systemSettingsValues.enable_outbound_recording,
  enable_internal_recording: systemSettingsValues.enable_internal_recording,
  enable_intercom: systemSettingsValues.enable_intercom,
  enable_vm_transcription: systemSettingsValues.enable_vm_transcription,
  schedules_v2: systemSettingsValues.schedules_v2,
  enable_agent_statuses: systemSettingsValues.enable_agent_statuses,
  enable_call_analytics: systemSettingsValues.enable_call_analytics,
  alert_911: systemSettingsValues.alert_911.trim(),
})

export const getChangedPayload = (
  initialPayload: Record<string, unknown>,
  currentPayload: Record<string, unknown>,
): Record<string, unknown> => {
  const changedPayload: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(currentPayload)) {
    if (value === undefined) continue
    if (JSON.stringify(value) !== JSON.stringify(initialPayload[key])) {
      changedPayload[key] = value
    }
  }
  return changedPayload
}

export const buildInitialFormState = (
  pbx: PbxV2,
  systemSettings: PbxSystemSettingsV2,
): EditPbxFormState => ({
  pbx: {
    name: pbx.name ?? '',
    company_id: toSelectValue(pbx.company_id),
    company_name: pbx.company_name ?? '',
    server_group_id: toSelectValue(pbx.server_group_id),
    provider_gateway_id: toSelectValue(pbx.provider_gateway_id),
    emergency_provider_gateway_id: toSelectValue(
      pbx.emergency_provider_gateway_id,
    ),
    international_provider_gateway_id: toSelectValue(
      pbx.international_provider_gateway_id,
    ),
    active: Boolean(pbx.active),
    maximum_allowed_channels: toNumberInputValue(pbx.maximum_allowed_channels),
    maximum_lines_in: toNumberInputValue(pbx.maximum_lines_in),
    maximum_lines_out: toNumberInputValue(pbx.maximum_lines_out),
    call_log_retention_period_in_months: toNumberInputValue(
      pbx.call_log_retention_period_in_months,
    ),
    industry_type: pbx.industry_type ?? '',
    ehr_type: pbx.ehr_type ?? '',
    preferred_codec: pbx.preferred_codec ?? '',
    international: Boolean(pbx.international),
    is_grove: Boolean(pbx.features?.is_grove),
    day_night: Boolean(pbx.day_night),
    day_night_slots: Array.from({ length: DAY_NIGHT_SLOT_COUNT }, (_, i) =>
      Boolean((pbx as unknown as Record<string, unknown>)[`day_night_${i}`]),
    ),
  },
  systemSettings: {
    default_time_zone: systemSettings.default_time_zone ?? '',
    extension_digits: toNumberInputValue(systemSettings.extension_digits),
    default_area_code: systemSettings.default_area_code ?? '',
    default_caller_id: toSelectValue(systemSettings.default_caller_id),
    default_send_fax_number: toSelectValue(
      systemSettings.default_send_fax_number,
    ),
    default_fax_extension_id: toSelectValue(
      systemSettings.default_fax_extension_id,
    ),
    default_hold_music_playlist: toSelectValue(
      systemSettings.default_hold_music_playlist,
    ),
    default_emergency_number: systemSettings.default_emergency_number ?? '',
    default_emergency_name: systemSettings.default_emergency_name ?? '',
    call_park_timeout: normalizeCallParkTimeoutForLegacy(
      systemSettings.call_park_timeout,
    ),
    enable_inbound_recording: Boolean(systemSettings.enable_inbound_recording),
    enable_outbound_recording: Boolean(
      systemSettings.enable_outbound_recording,
    ),
    enable_internal_recording: Boolean(
      systemSettings.enable_internal_recording,
    ),
    enable_intercom: Boolean(systemSettings.enable_intercom),
    enable_vm_transcription: Boolean(systemSettings.enable_vm_transcription),
    schedules_v2: Boolean(systemSettings.schedules_v2),
    enable_agent_statuses: Boolean(systemSettings.enable_agent_statuses),
    enable_call_analytics: Boolean(systemSettings.enable_call_analytics),
    alert_911: systemSettings.alert_911 ?? '',
  },
})

export const mapPaginatedResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[]
  return ((data as PaginatedApiResult<T>).results ?? []) as T[]
}
